/** @jsxImportSource @emotion/react */
import React from "react";
import {
  Drawer,
  Input,
  Select,
  Button,
  Space,
  Typography,
  Row,
  Col,
  message,
  Spin,
  Checkbox,
  InputNumber,
  Form,
  Upload,
} from "antd";
import {
  DeleteOutlined,
  PlusOutlined,
  UploadOutlined,
} from "@ant-design/icons";
import { arrayMove } from "@dnd-kit/sortable";
import { OptionTable } from "./OptionTable";
import { MapNodeEditor } from "./MapNodeEditor";
import { TeleportNodeEditor } from "./TeleportNodeEditor";
import { TriggerNodeEditor } from "./TriggerNodeEditor";
import { VisibilityNodeEditor } from "./VisibilityNodeEditor";
import { BaseOptionEditor } from "./BaseOptionEditor";
import { OptionTagsInput } from "./OptionTagsInput";
import * as Styles from "./styles";
import { MAX_UPLOAD_BYTES } from "./constants";
import {
  ConfigNodeType,
  ConfigOption,
  ConfigOptionDependency,
  ConfigurationType,
  DependencyGroupValue,
  HomeConfigFormData,
  NodeBucket,
  OptionLayout,
  TeleportConfigNode,
  TriggerConfigNode,
  Vector3,
  VisibilityConfigNode,
} from "../../../usecases/HomeConfig/types";
import { DraftConfigNode, DraftMapConfigNode, DraftOption } from "./types";
import {
  configurationTypeLabel,
  layoutOptions,
  RESERVED_NAME_PREFIX,
} from "../../../usecases/HomeConfig/constant";
import {
  TagKeysAndValues,
  FreeformValueKeys,
} from "../../../usecases/HomeConfig/tagKeys";
import { GetHomeDto } from "../../../services/Home/dto/get.dto";
import { GetMaterialDto } from "../../../services/Material/dto/get.dto";
import { GetProductDto } from "../../../services/Product/dto/get.dto";
import { CheckboxChangeEvent } from "antd/lib/checkbox";
import { RcFile } from "antd/lib/upload";
import { LocalStorage } from "../../../modules/LocalStorage";
import { HomeConfigUsecase } from "../../../usecases/HomeConfig/usecase";
import { HomeConfigUtil } from "../../../usecases/HomeConfig/utils";
import { KeyValueInput, KeyValuePair } from "../../KeyValueInput";
import { ArrayInput } from "../../ArrayInput";
import { Vector3Input } from "../../Vector3Input";
import {
  KeyValueSelectInput,
  KeyValueSelectPair,
} from "../../KeyValueSelectInput";

interface DrawerHomeConfigurationProps {
  visible: boolean;
  onClose: () => void;
  onSave: (config: HomeConfigFormData) => void;
  loading?: boolean;
  homeDataList?: GetHomeDto[];
  takenHomeIds?: string[];
  materials?: GetMaterialDto[];
  products?: GetProductDto[];
  editingConfig?: HomeConfigFormData;
  defaultType?: ConfigurationType;
  defaultHomeId?: string;
  defaultOptions?: ConfigOption[];
  variant?: "options" | "baseOption";
}

const recordToPairs = (record: Record<string, string>): KeyValuePair[] => {
  return Object.entries(record || {}).map(([key, value]) => {
    return { key, value };
  });
};

const pairsToRecord = (pairs: KeyValuePair[]): Record<string, string> => {
  return pairs.reduce((acc, pair) => {
    if (pair.key) {
      acc[pair.key] = pair.value;
    }
    return acc;
  }, {} as Record<string, string>);
};

const recordToPairsSelect = (
  record: Record<string, DependencyGroupValue>,
): KeyValueSelectPair[] => {
  return Object.entries(record || {}).map(([key, value]) => {
    return { key, value: value.value, isSelect: value.isSelect };
  });
};

const pairsSelectToRecord = (
  pairs: KeyValueSelectPair[],
): Record<string, DependencyGroupValue> => {
  return pairs.reduce((acc, pair) => {
    if (pair.key) {
      acc[pair.key] = { value: pair.value, isSelect: pair.isSelect };
    }
    return acc;
  }, {} as Record<string, DependencyGroupValue>);
};

const emptyNode = (type: ConfigNodeType): DraftConfigNode => {
  switch (type) {
    case "material":
      return {
        type: "material",
        materialName: "",
        materialId: "",
        filterBy: {},
      };
    case "mesh":
      return { type: "mesh", meshName: "", filterBy: {}, dependencyGroup: {} };
    case "visibility":
      return { type: "visibility", filterBy: {}, tags: {} };
    case "transform":
      return {
        type: "transform",
        filterBy: {},
        locationOffset: { x: 0, y: 0, z: 0 },
      };
    case "map":
      return { type: "map", filterBy: {}, floorMaps: [], areaHighlights: [] };
    case "teleport":
      return {
        type: "teleport",
        location: { x: 0, y: 0, z: 0 },
        rotation: { x: 0, y: 0, z: 0 },
      };
    case "trigger":
      return { type: "trigger", options: {} };
  }
};

type NodeOptions = {
  label: string;
  value: ConfigNodeType;
};

const nodeTypeOptions: NodeOptions[] = [
  { label: "Material", value: "material" },
  { label: "Visibility", value: "visibility" },
  { label: "Mesh", value: "mesh" },
  { label: "Transform", value: "transform" },
  { label: "Map", value: "map" },
  { label: "Teleport", value: "teleport" },
  { label: "Trigger", value: "trigger" },
];

const configurationTypes: ConfigurationType[] = [
  "base",
  "region",
  "community",
  "user",
];

const DrawerHomeConfigurationComponent: React.FC<DrawerHomeConfigurationProps> =
  ({
    visible,
    onClose,
    onSave,
    loading = false,
    homeDataList: homes,
    takenHomeIds = [],
    materials = [],
    products = [],
    editingConfig,
    defaultType = "base",
    defaultHomeId,
    defaultOptions,
    variant = "options",
  }: DrawerHomeConfigurationProps) => {
    const isEdit = Boolean(editingConfig);

    const [homeId, setHomeId] = React.useState<string | undefined>();
    const [type, setType] = React.useState<ConfigurationType>("base");
    const [scopeName, setScopeName] = React.useState("");

    const [options, setOptions] = React.useState<DraftOption[]>([]);
    const [uploading, setUploading] = React.useState<boolean>(false);

    const [optionName, setOptionName] = React.useState("");
    const [customGroup, setCustomGroup] = React.useState("");
    const [dependency, setDependency] = React.useState<ConfigOptionDependency>(
      {},
    );
    const [layout, setLayout] = React.useState<OptionLayout | undefined>();
    const [thumbnail, setThumbnail] = React.useState<string>();
    const [thumbnailFile, setThumbnailFile] = React.useState<File>();
    const [optionTags, setOptionTags] = React.useState<
      Record<string, string | number>
    >({});
    const [displayKey, setDisplayKey] = React.useState("");
    const [optionValue, setOptionValue] = React.useState("");
    const [uncheckedValue, setUncheckedValue] = React.useState("");
    const [useHighlightEffect, setUseHighlightEffect] = React.useState(false);
    const [highlightGroup, setHighlightGroup] = React.useState(0);
    const [show2DMap, setShow2DMap] = React.useState(false);
    const [nodeType, setNodeType] = React.useState<ConfigNodeType>("material");
    const [uncheckedNodeType, setUncheckedNodeType] =
      React.useState<ConfigNodeType>("material");
    const [nodes, setNodes] = React.useState<DraftConfigNode[]>([]);
    const [uncheckedNodes, setUncheckedNodes] = React.useState<
      DraftConfigNode[]
    >([]);
    const [editingOptionIndex, setEditingOptionIndex] = React.useState<
      number | null
    >(null);
    const topRef = React.useRef<HTMLDivElement>(null);

    const optionGroups = HomeConfigUtil.getGroups(options);

    const refPaths = HomeConfigUtil.refPaths(options);
    const refSources: Record<string, string[]> = {};
    Object.keys(TagKeysAndValues).forEach((key) => {
      refSources[key] = refPaths;
    });

    const resetOptionEditor = () => {
      setOptionName("");
      setCustomGroup("");
      setLayout(undefined);
      setThumbnail(undefined);
      setThumbnailFile(undefined);
      setOptionTags({});
      setDisplayKey("");
      setOptionValue("");
      setUncheckedValue("");
      setUseHighlightEffect(false);
      setHighlightGroup(0);
      setShow2DMap(false);
      setDependency({});
      setNodes([]);
      setUncheckedNodes([]);
      setNodeType("material");
      setUncheckedNodeType("material");
      setEditingOptionIndex(null);
    };

    React.useEffect(() => {
      if (!visible) {
        return;
      }
      if (editingConfig) {
        setHomeId(editingConfig.homeId);
        setType(editingConfig.type);
        setScopeName(editingConfig.scopeName || "");
        setOptions(
          HomeConfigUtil.refsToIds(
            editingConfig.options || [],
            editingConfig.type === "base",
          ),
        );
      } else {
        setHomeId(defaultHomeId);
        setType(defaultType);
        setScopeName("");
        setOptions(
          HomeConfigUtil.refsToIds(
            defaultOptions ?? [],
            defaultType === "base",
          ),
        );
      }
      resetOptionEditor();
    }, [visible, editingConfig, defaultType, defaultHomeId, defaultOptions]);

    const setBucket = (bucket: NodeBucket) => {
      return bucket === "nodes" ? setNodes : setUncheckedNodes;
    };

    const onAddNode = (bucket: NodeBucket) => {
      const type = bucket === "nodes" ? nodeType : uncheckedNodeType;
      setBucket(bucket)((prev) => {
        return [...prev, emptyNode(type)];
      });
    };

    const updateNode = (
      index: number,
      updater: (node: DraftConfigNode) => DraftConfigNode,
      bucket: NodeBucket,
    ) => {
      setBucket(bucket)((prev) => {
        return prev.map((node, i) => {
          if (i === index) {
            return updater(node);
          }
          return node;
        });
      });
    };

    const onRemoveNode = (index: number, bucket: NodeBucket) => {
      setBucket(bucket)((prev) => {
        return prev.filter((_, i) => {
          return i !== index;
        });
      });
    };

    const onCommitOption = () => {
      if (!optionName.trim()) {
        message.warning("Option Name is required");
        return;
      }
      // NOTE: layout drives a field's existence, so switching away must not
      // smuggle data the buyer will never render into the config.
      const keepUncheckedNodes =
        layout === "list-row" && uncheckedNodes.length > 0;
      if (layout !== "list-row" && uncheckedNodes.length > 0) {
        message.warning(
          'Unchecked nodes are dropped — layout is not "list-row"',
        );
      }
      const trimmedName = optionName.trim();
      // NOTE: the "__" prefix is what makes the home-wide config option
      // unforgeable, so no authored name may claim it.
      const isReservedName =
        variant === "options" &&
        trimmedName.toLowerCase().startsWith(RESERVED_NAME_PREFIX);
      if (isReservedName) {
        message.warning(
          `Option Name cannot start with "${RESERVED_NAME_PREFIX}"`,
        );
        return;
      }
      const isDuplicateName = options.some(
        (o, i) => o.name === trimmedName && i !== editingOptionIndex,
      );
      if (isDuplicateName) {
        message.warning("Option Name must be unique");
        return;
      }
      const tags = Object.keys(optionTags).length > 0 ? optionTags : undefined;
      const newId = type === "base" ? crypto.randomUUID() : undefined;
      const draft: DraftOption = {
        id:
          editingOptionIndex !== null ? options[editingOptionIndex].id : newId,
        name: trimmedName,
        order: 0,
        customGroup: customGroup.trim(),
        price: 0,
        dependency: dependency,
        useHighlightEffect: useHighlightEffect,
        highlightGroup: highlightGroup,
        show2DMap: show2DMap,
        value: optionValue.trim() || undefined,
        nodes,
        ...(keepUncheckedNodes ? { uncheckedNodes } : {}),
        // JSON.stringify drops the undefined half on the way to S3.
        ...(layout || thumbnail || tags
          ? {
              presentation: {
                layout,
                thumbnail,
                tags,
                // NOTE: a key the buyer cannot resolve prints nothing, so a stale
                // pick is dropped rather than carried — the tag it named is gone.
                displayKey: tags?.[displayKey] ? displayKey : undefined,
                uncheckedValue:
                  layout === "list-row"
                    ? uncheckedValue.trim() || undefined
                    : undefined,
              },
            }
          : {}),
        ...(thumbnailFile ? { thumbnailFile } : {}),
      };
      const option = HomeConfigUtil.toIdRefs(draft, options);
      setOptions((prev) => {
        if (editingOptionIndex !== null) {
          return prev.map((o, i) => {
            if (i === editingOptionIndex) {
              return option;
            }
            return o;
          });
        }
        return [...prev, option];
      });
      resetOptionEditor();
    };

    const onEditOption = (index: number) => {
      const option = options[index];
      const named = HomeConfigUtil.toNameRefs(option, options);
      setOptionName(option.name);
      setCustomGroup(option.customGroup ?? "");
      setLayout(option.presentation?.layout);
      setThumbnail(option.presentation?.thumbnail);
      setThumbnailFile(option.thumbnailFile);
      setOptionTags(option.presentation?.tags ?? {});
      setDisplayKey(option.presentation?.displayKey ?? "");
      setOptionValue(option.value ?? "");
      setUncheckedValue(option.presentation?.uncheckedValue ?? "");
      setUseHighlightEffect(option.useHighlightEffect ?? false);
      setHighlightGroup(option.highlightGroup ?? 0);
      setShow2DMap(option.show2DMap ?? false);
      setDependency(named.dependency);
      setNodes(named.nodes);
      setUncheckedNodes(named.uncheckedNodes ?? []);
      setNodeType(option.nodes[0]?.type ?? "material");
      setUncheckedNodeType(option.uncheckedNodes?.[0]?.type ?? "material");
      setEditingOptionIndex(index);
      topRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    };

    const onDeleteOption = (index: number) => {
      setOptions((prev) => {
        const rest = prev.filter((_, i) => {
          return i !== index;
        });
        return HomeConfigUtil.reanchorRefs(rest, prev[index]);
      });
      if (editingOptionIndex === index) {
        resetOptionEditor();
      }
    };

    const onReorderOption = (from: number, to: number) => {
      setOptions((prev) => arrayMove(prev, from, to));
      // Keep the option editor's target row in sync if one is open.
      setEditingOptionIndex((current) => {
        if (current === null) {
          return current;
        }
        if (current === from) {
          return to;
        }
        if (from < current && current <= to) {
          return current - 1;
        }
        if (to <= current && current < from) {
          return current + 1;
        }
        return current;
      });
    };

    const onSaveHandle = async () => {
      if (!homeId) {
        message.warning("Please pick a Home");
        return;
      }
      if (type !== "base" && !scopeName.trim()) {
        message.warning(
          `Please input the ${configurationTypeLabel[type]} name`,
        );
        return;
      }
      if (variant === "options" && options.length === 0) {
        message.warning("Add at least one option");
        return;
      }
      const home = homes.find((homeItem) => {
        return homeItem.id === homeId;
      });
      if (!home) {
        message.error("Selected home is no longer available");
        return;
      }
      const tenantId = LocalStorage.getTenantInfo()?.tenantId;
      if (!tenantId) {
        message.error("Missing tenant info, please sign in again");
        return;
      }

      const homeConfigUsecase = new HomeConfigUsecase();
      setUploading(true);
      try {
        // NOTE: One image is often shared by many options, so each url is
        // localized once per save rather than once per reference.
        const localized = new Map<string, Promise<string>>();
        const localize = (url: string) => {
          const pending =
            localized.get(url) ??
            homeConfigUsecase.localizeImage({ tenantId, url });
          localized.set(url, pending);
          return pending;
        };
        const uploadNodes = async (list: DraftConfigNode[]) => {
          return Promise.all(
            list.map(async (node) => {
              if (node.type !== "map") {
                return node;
              }
              const floorMaps = await Promise.all(
                node.floorMaps.map(async ({ imageFile, ...floor }) => {
                  const imageUrl = imageFile
                    ? await homeConfigUsecase.uploadImage({
                        tenantId,
                        file: imageFile,
                      })
                    : floor.imageUrl && (await localize(floor.imageUrl));
                  return { ...floor, imageUrl };
                }),
              );
              return { ...node, floorMaps };
            }),
          );
        };
        const uploaded = await Promise.all(
          options.map(async ({ thumbnailFile: file, ...option }) => {
            const nodes = await uploadNodes(option.nodes ?? []);
            const uncheckedNodes = option.uncheckedNodes
              ? await uploadNodes(option.uncheckedNodes)
              : undefined;
            const currentThumbnail = option.presentation?.thumbnail;
            const thumbnail = file
              ? await homeConfigUsecase.uploadImage({ tenantId, file })
              : currentThumbnail && (await localize(currentThumbnail));
            return {
              ...option,
              nodes,
              ...(uncheckedNodes ? { uncheckedNodes } : {}),
              ...(thumbnail
                ? { presentation: { ...option.presentation, thumbnail } }
                : {}),
            };
          }),
        );
        const config: HomeConfigFormData = {
          id: editingConfig?.id || `cfg-${Date.now()}`,
          homeId,
          homeName: home.homeName,
          type,
          scopeName: type === "base" ? undefined : scopeName.trim(),
          options: uploaded,
        };
        onSave(config);
      } catch (error) {
        message.error(error.message);
      } finally {
        setUploading(false);
      }
    };

    // false: antd must not upload, the file waits for Save.
    const handlePickThumbnail = (file: RcFile) => {
      if (file.size > MAX_UPLOAD_BYTES) {
        message.warning("Thumbnail must be 5MB or smaller");
      } else {
        setThumbnailFile(file);
      }
      return false;
    };

    // NOTE: false keeps antd from uploading; the file is only read here.
    const handleImportFile = async (file: RcFile) => {
      const isBaseImport = variant === "baseOption";
      try {
        const text = await file.text();
        const imported = HomeConfigUtil.parseOptions(text);
        const picked = imported.filter((option) => {
          return HomeConfigUtil.isBaseOption(option) === isBaseImport;
        });
        if (picked.length === 0) {
          message.error(
            isBaseImport ? "File has no base option" : "File has no options",
          );
          return false;
        }
        resetOptionEditor();
        setOptions((prev) => {
          const kept = prev.filter((option) => {
            return HomeConfigUtil.isBaseOption(option) !== isBaseImport;
          });
          return HomeConfigUtil.refsToIds(
            [...kept, ...picked],
            type === "base",
          );
        });
        message.success("Imported configuration, review then Save");
      } catch (error) {
        message.error(error.message);
      }
      return false;
    };

    const handleHomeIdChange = (value: unknown) => {
      if (typeof value === "string") {
        setHomeId(value);
      }
    };

    const handleScopeNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      setScopeName(e.target.value);
    };

    const handleOptionNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      setOptionName(e.target.value);
    };

    const handleCustomGroupChange = (
      e: React.ChangeEvent<HTMLInputElement>,
    ) => {
      setCustomGroup(e.target.value);
    };

    const handleOptionValueChange = (
      e: React.ChangeEvent<HTMLInputElement>,
    ) => {
      setOptionValue(e.target.value);
    };

    const handleUncheckedValueChange = (
      e: React.ChangeEvent<HTMLInputElement>,
    ) => {
      setUncheckedValue(e.target.value);
    };

    const onUseHighlightEffectChange = (e: CheckboxChangeEvent) => {
      setUseHighlightEffect(e.target.checked);
    };

    const onHighlightGroupChange = (value: number) => {
      setHighlightGroup(value);
    };

    const handleNodeTypeChange = (value: ConfigNodeType) => {
      setNodeType(value);
    };

    const handleUncheckedNodeTypeChange = (value: ConfigNodeType) => {
      setUncheckedNodeType(value);
    };

    const handleAddNode = () => {
      onAddNode("nodes");
    };

    const handleAddUncheckedNode = () => {
      onAddNode("uncheckedNodes");
    };

    const scopeNameInput =
      type !== "base" ? (
        <Col span={8}>
          <Typography.Text type="secondary">
            {configurationTypeLabel[type]} Name
          </Typography.Text>
          <Input
            placeholder={
              type === "region" ? "e.g. US Region" : "e.g. Project A"
            }
            value={scopeName}
            disabled={isEdit}
            onChange={handleScopeNameChange}
          />
        </Col>
      ) : null;

    const commitOptionBtnText =
      editingOptionIndex !== null ? "UPDATE" : "ADD OPTION";

    // A picked file has no URL until Save uploads it, so it previews from memory.
    const thumbnailPreview = React.useMemo(() => {
      return thumbnailFile ? URL.createObjectURL(thumbnailFile) : thumbnail;
    }, [thumbnailFile, thumbnail]);

    const editingOptionName =
      editingOptionIndex !== null
        ? options[editingOptionIndex]?.name
        : undefined;

    const materialOptions = materials.map((material) => {
      return {
        label: `${material.materialKey} - ${material.name}`,
        value: material.materialKey,
      };
    });

    const productFileOptions = products
      .filter((product) => {
        return product.productFileName;
      })
      .map((product) => {
        return {
          label: `${product.productKey} - ${product.name}`,
          value: product.productFileName,
        };
      });

    const handleAddDependencyTrigger = (value: string) => {
      setDependency((prev) => {
        const newTriggers = [];
        if (prev?.triggers?.length > 0) {
          newTriggers.push(...prev.triggers);
        }
        newTriggers.push(value);
        return {
          ...prev,
          triggers: newTriggers,
        };
      });
    };

    const handleRemoveDependencyTrigger = (index: number) => {
      setDependency((prev) => {
        const newTriggers = prev.triggers;
        newTriggers.splice(index, 1);
        return {
          ...prev,
          triggers: newTriggers,
        };
      });
    };

    const renderNodeBlock = (
      node: DraftConfigNode,
      index: number,
      bucket: NodeBucket,
    ) => {
      const handleRemoveNode = () => {
        onRemoveNode(index, bucket);
      };

      // NOTE: binds this block's row and list once so every editor below keeps
      // the two-argument call shape, whichever list it is rendered into.
      const update = (updater: (node: DraftConfigNode) => DraftConfigNode) => {
        updateNode(index, updater, bucket);
      };

      // NOTE: Filter By / Dependency Group live on every node type but teleport
      // and trigger, whose editors hide them — narrowing the union here keeps the
      // guard out of the updaters below.
      const updateMetaNode = (
        updater: (
          node: Exclude<
            DraftConfigNode,
            TeleportConfigNode | TriggerConfigNode
          >,
        ) => DraftConfigNode,
      ) => {
        update((currentNode) => {
          if (
            currentNode.type === "teleport" ||
            currentNode.type === "trigger"
          ) {
            return currentNode;
          }
          return updater(currentNode);
        });
      };

      const handleAddFilter = (pair: KeyValuePair) => {
        updateMetaNode((currentNode) => {
          return {
            ...currentNode,
            filterBy: {
              ...currentNode.filterBy,
              [pair.key]: pair.value,
            },
          };
        });
      };

      const handleRemoveFilter = (pairIndex: number) => {
        updateMetaNode((currentNode) => {
          const pairs = recordToPairs(currentNode.filterBy).filter((_, i) => {
            return i !== pairIndex;
          });
          return {
            ...currentNode,
            filterBy: pairsToRecord(pairs),
          };
        });
      };

      const handleVisibilityChange = (
        updater: (node: VisibilityConfigNode) => VisibilityConfigNode,
      ) => {
        update((currentNode) => {
          if (currentNode.type !== "visibility") {
            return currentNode;
          }
          return updater(currentNode);
        });
      };

      const handleAddDependencyGroup = (pair: KeyValueSelectPair) => {
        updateMetaNode((currentNode) => {
          return {
            ...currentNode,
            dependencyGroup: {
              ...(currentNode.dependencyGroup || {}),
              [pair.key]: { value: pair.value, isSelect: pair.isSelect },
            },
          };
        });
      };

      const handleRemoveDependencyGroup = (pairIndex: number) => {
        updateMetaNode((currentNode) => {
          const pairs = recordToPairsSelect(
            currentNode.dependencyGroup || {},
          ).filter((_, i) => {
            return i !== pairIndex;
          });
          return {
            ...currentNode,
            dependencyGroup: pairsSelectToRecord(pairs),
          };
        });
      };

      const handleMaterialChange = (
        value: string,
        option:
          | { label: string; value: string }
          | { label: string; value: string }[],
      ) => {
        if (Array.isArray(option)) {
          return;
        }
        const selected = materials.find((material) => {
          return material.materialKey === value;
        });
        update((currentNode) => {
          return {
            ...currentNode,
            materialId: value,
            materialName: selected?.materialInstanceFileName ?? "",
          };
        });
      };

      const handleChangeLocationOffset = (locationOffset: Vector3) => {
        update((currentNode) => {
          if (currentNode.type !== "transform") {
            return currentNode;
          }
          return { ...currentNode, locationOffset };
        });
      };

      const savedMaterialId = node.type === "material" ? node.materialId : "";
      const savedMaterialName =
        node.type === "material" ? node.materialName : "";
      const isSavedMaterialMissing =
        savedMaterialId &&
        !materialOptions.some((option) => option.value === savedMaterialId);

      const nodeMaterialOptions = isSavedMaterialMissing
        ? [
            {
              label: savedMaterialName,
              value: savedMaterialId,
            },
            ...materialOptions,
          ]
        : materialOptions;

      const handleMeshNameChange = (value: string) => {
        update((currentNode) => {
          return { ...currentNode, meshName: value };
        });
      };

      const savedMeshName = node.type === "mesh" ? node.meshName : "";
      const isSavedMeshMissing =
        savedMeshName &&
        !productFileOptions.some((option) => option.value === savedMeshName);

      const nodeMeshOptions = isSavedMeshMissing
        ? [
            { label: savedMeshName, value: savedMeshName },
            ...productFileOptions,
          ]
        : productFileOptions;

      const meshSection =
        node.type === "mesh" ? (
          <Row gutter={8} align="middle" css={Styles.sectionStyle}>
            <Col span={6}>
              <Typography.Text type="secondary">Mesh Name</Typography.Text>
            </Col>
            <Col span={18}>
              <Select
                css={Styles.fullWidthStyle}
                showSearch
                optionFilterProp="label"
                placeholder="Search mesh..."
                value={node.meshName || undefined}
                onChange={handleMeshNameChange}
                options={nodeMeshOptions}
              />
            </Col>
          </Row>
        ) : null;

      const materialSection =
        node.type === "material" ? (
          <Row gutter={8} align="middle" css={Styles.sectionStyle}>
            <Col span={6}>
              <Typography.Text type="secondary">Material Name</Typography.Text>
            </Col>
            <Col span={18}>
              <Select
                css={Styles.fullWidthStyle}
                showSearch
                optionFilterProp="label"
                placeholder="Search material..."
                value={node.materialId || undefined}
                onChange={handleMaterialChange}
                options={nodeMaterialOptions}
              />
            </Col>
          </Row>
        ) : null;

      const visibilitySection =
        node.type === "visibility" ? (
          <VisibilityNodeEditor
            node={node}
            refSources={refSources}
            onChange={handleVisibilityChange}
          />
        ) : null;

      const dependencyGroupSection =
        node.type === "mesh" || node.type === "map" ? (
          <KeyValueSelectInput
            label="Dependency Group"
            sourceKeyValues={optionGroups}
            pairs={recordToPairsSelect(node.dependencyGroup || {})}
            hasAnyValue={false}
            onAdd={handleAddDependencyGroup}
            onRemove={handleRemoveDependencyGroup}
          />
        ) : null;

      const transformSection =
        node.type === "transform" ? (
          <Vector3Input
            label="Location Offset"
            value={node.locationOffset}
            onChange={handleChangeLocationOffset}
          />
        ) : null;

      const handleTeleportChange = (
        updater: (node: TeleportConfigNode) => TeleportConfigNode,
      ) => {
        update((currentNode) => {
          if (currentNode.type !== "teleport") {
            return currentNode;
          }
          return updater(currentNode);
        });
      };

      const teleportSection =
        node.type === "teleport" ? (
          <TeleportNodeEditor node={node} onChange={handleTeleportChange} />
        ) : null;

      const handleTriggerChange = (
        updater: (node: TriggerConfigNode) => TriggerConfigNode,
      ) => {
        update((currentNode) => {
          if (currentNode.type !== "trigger") {
            return currentNode;
          }
          return updater(currentNode);
        });
      };

      const triggerSection =
        node.type === "trigger" ? (
          <TriggerNodeEditor
            node={node}
            optionGroups={optionGroups}
            onChange={handleTriggerChange}
          />
        ) : null;

      const handleMapChange = (
        updater: (node: DraftMapConfigNode) => DraftMapConfigNode,
      ) => {
        update((currentNode) => {
          if (currentNode.type !== "map") {
            return currentNode;
          }
          return updater(currentNode);
        });
      };

      const mapSection =
        node.type === "map" ? (
          <MapNodeEditor node={node} onChange={handleMapChange} />
        ) : null;

      const filterSection =
        node.type === "map" ||
        node.type === "teleport" ||
        node.type === "trigger" ? null : (
          <KeyValueInput
            label="Filter By"
            sourceKeyValues={TagKeysAndValues}
            pairs={recordToPairs(node.filterBy)}
            freeformKeys={FreeformValueKeys}
            onAdd={handleAddFilter}
            onRemove={handleRemoveFilter}
          />
        );

      return (
        <div css={Styles.nodeBlockStyle} key={index}>
          <DeleteOutlined
            css={Styles.removeIconStyle}
            onClick={handleRemoveNode}
          />
          <div css={Styles.nodeTitleStyle}>{node.type}</div>
          {materialSection}
          {meshSection}
          {filterSection}
          {visibilitySection}
          {dependencyGroupSection}
          {transformSection}
          {teleportSection}
          {triggerSection}
          {mapSection}
        </div>
      );
    };

    // Creating a new base config: hide homes that already have one so the user
    // can't pick a duplicate. Child (region/community/user) and edit flows keep
    // the full list — their home has a base config by design.
    const isCreatingBase = type === "base" && !isEdit;
    const homeOptions = homes
      .filter((homeItem) => {
        return !isCreatingBase || !takenHomeIds.includes(homeItem.id);
      })
      .map((homeItem) => {
        return {
          label: homeItem.homeName,
          value: homeItem.id,
        };
      });

    const isBaseOptionVariant = variant === "baseOption";

    const drawerTitle = isBaseOptionVariant
      ? "Base Option"
      : isEdit
      ? "Edit Configuration"
      : "Home Configuration Creator";

    return (
      <Drawer
        title={drawerTitle}
        width="100%"
        open={visible}
        onClose={onClose}
        footer={
          <Space css={Styles.floatRightStyle}>
            <Upload
              accept=".json,application/json"
              showUploadList={false}
              beforeUpload={handleImportFile}
            >
              <Button icon={<UploadOutlined />} disabled={loading || uploading}>
                Import
              </Button>
            </Upload>
            <Button onClick={onClose} disabled={loading || uploading}>
              Cancel
            </Button>
            <Button
              type="primary"
              onClick={onSaveHandle}
              loading={loading || uploading}
            >
              Save
            </Button>
          </Space>
        }
      >
        <Spin spinning={loading || uploading}>
          <div ref={topRef} />
          <Row gutter={16} css={Styles.sectionStyle}>
            <Col span={8}>
              <Typography.Text type="secondary">Home</Typography.Text>
              <Select
                css={Styles.fullWidthStyle}
                placeholder="Pick a home"
                value={homeId}
                disabled={isEdit}
                onChange={handleHomeIdChange}
                options={homeOptions}
              />
            </Col>
            <Col span={8}>
              <Typography.Text type="secondary">Type</Typography.Text>
              <Select
                css={Styles.fullWidthStyle}
                value={type}
                disabled={isBaseOptionVariant}
                options={configurationTypes.map((t) => {
                  return {
                    label: configurationTypeLabel[t],
                    value: t,
                  };
                })}
              />
            </Col>
            {scopeNameInput}
          </Row>

          {isBaseOptionVariant && (
            <BaseOptionEditor
              visible={visible}
              options={options}
              onOptionsChange={setOptions}
            />
          )}

          {!isBaseOptionVariant && (
            <div css={Styles.nodeEditorStyle}>
              <Row gutter={16} css={Styles.sectionStyle}>
                <Col span={12}>
                  <Typography.Text type="secondary">
                    Option Name
                  </Typography.Text>
                  <Input
                    placeholder="e.g. Walls | Great Room | Blue"
                    value={optionName}
                    onChange={handleOptionNameChange}
                  />
                </Col>
                <Col span={12}>
                  <Typography.Text type="secondary">
                    Custom Group (optional)
                  </Typography.Text>
                  <Input
                    placeholder="Defaults to parent node name"
                    value={customGroup}
                    onChange={handleCustomGroupChange}
                  />
                </Col>
              </Row>
              <Row gutter={16} css={Styles.sectionStyle}>
                <Col span={12}>
                  <Typography.Text type="secondary">
                    Layout (optional)
                  </Typography.Text>
                  <Select
                    allowClear
                    css={Styles.fullWidthStyle}
                    placeholder="Default"
                    value={layout}
                    onChange={setLayout}
                    options={layoutOptions}
                  />
                </Col>
                <Col span={12}>
                  <Typography.Text type="secondary">
                    Thumbnail (optional)
                  </Typography.Text>
                  <br />
                  {/* The tile itself is the trigger, so picking again replaces. */}
                  <Upload
                    listType="picture-card"
                    accept="image/png,image/jpeg,image/svg+xml"
                    showUploadList={false}
                    beforeUpload={handlePickThumbnail}
                  >
                    {thumbnailPreview ? (
                      <img
                        src={thumbnailPreview}
                        alt="Option thumbnail"
                        css={Styles.thumbnailPreviewStyle}
                      />
                    ) : (
                      <PlusOutlined />
                    )}
                  </Upload>
                </Col>
              </Row>
              <Row gutter={16} css={Styles.sectionStyle}>
                <Col span={12}>
                  <Typography.Text type="secondary">
                    Value (optional)
                  </Typography.Text>
                  <Input
                    placeholder="Actor tag value, e.g. Prep_Kitchen"
                    value={optionValue}
                    onChange={handleOptionValueChange}
                  />
                </Col>
                {layout === "list-row" && (
                  <Col span={12}>
                    <Typography.Text type="secondary">
                      Unchecked Value (optional)
                    </Typography.Text>
                    <Input
                      placeholder="Actor tag value when off, e.g. Standard_Kitchen"
                      value={uncheckedValue}
                      onChange={handleUncheckedValueChange}
                    />
                  </Col>
                )}
              </Row>
              <OptionTagsInput
                tags={optionTags}
                displayKey={displayKey}
                onTagsChange={setOptionTags}
                onDisplayKeyChange={setDisplayKey}
              />
              <Row gutter={16} css={Styles.sectionStyle}>
                <Col span={12}>
                  <Typography.Text type="secondary">Options</Typography.Text>
                  <br />
                  <Row>
                    <Col span={12}>
                      <Form.Item label="Use Highlight Effect">
                        <Checkbox
                          checked={useHighlightEffect}
                          onChange={onUseHighlightEffectChange}
                        />
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item label="Highlight Group">
                        <InputNumber
                          value={highlightGroup || 0}
                          onChange={onHighlightGroupChange}
                        />
                      </Form.Item>
                    </Col>
                  </Row>
                </Col>
              </Row>
              <Row gutter={16} css={Styles.sectionStyle}>
                <Col span={12}>
                  <Typography.Text type="secondary">Dependency</Typography.Text>
                  <ArrayInput
                    label="Triggers"
                    values={dependency?.triggers || []}
                    options={Object.keys(optionGroups)}
                    onAdd={handleAddDependencyTrigger}
                    onRemove={handleRemoveDependencyTrigger}
                  />
                </Col>
              </Row>
              {layout === "list-row" && (
                <>
                  <Row gutter={16} align="bottom" css={Styles.sectionStyle}>
                    <Col span={12}>
                      <Typography.Text type="secondary">
                        Unchecked Node Type
                      </Typography.Text>
                      <Select
                        css={Styles.fullWidthStyle}
                        value={uncheckedNodeType}
                        onChange={handleUncheckedNodeTypeChange}
                        options={nodeTypeOptions}
                      />
                    </Col>
                    <Col span={12}>
                      <Button
                        icon={<PlusOutlined />}
                        onClick={handleAddUncheckedNode}
                      >
                        ADD UNCHECKED NODE
                      </Button>
                    </Col>
                  </Row>

                  {uncheckedNodes.map((node, index) => {
                    return renderNodeBlock(node, index, "uncheckedNodes");
                  })}
                </>
              )}

              <Row gutter={16} align="bottom" css={Styles.sectionStyle}>
                <Col span={12}>
                  <Typography.Text type="secondary">Node Type</Typography.Text>
                  <Select
                    css={Styles.fullWidthStyle}
                    value={nodeType}
                    onChange={handleNodeTypeChange}
                    options={nodeTypeOptions}
                  />
                </Col>
                <Col span={12}>
                  <Button icon={<PlusOutlined />} onClick={handleAddNode}>
                    ADD NODE
                  </Button>
                </Col>
              </Row>

              {nodes.map((node, index) => {
                return renderNodeBlock(node, index, "nodes");
              })}

              <Space>
                <Button type="primary" onClick={onCommitOption}>
                  {commitOptionBtnText}
                </Button>
                <Button onClick={resetOptionEditor}>RESET</Button>
              </Space>
            </div>
          )}

          {!isBaseOptionVariant && (
            <OptionTable
              key={`${editingConfig?.id ?? "create"}-${visible}`}
              options={options}
              editingOptionName={editingOptionName}
              onEditOption={onEditOption}
              onDeleteOption={onDeleteOption}
              onReorderOption={onReorderOption}
            />
          )}
        </Spin>
      </Drawer>
    );
  };

export const DrawerHomeConfiguration = React.memo(
  DrawerHomeConfigurationComponent,
);
